import { AssistantPanel } from "@gtm-agents/gtm-ui/chat";
import { ActivityDrawer, CommandPalette, useShortcuts } from "@gtm-agents/gtm-ui/command";
import { ErrorBoundary } from "@gtm-agents/gtm-ui/layout";
import { COLORS, TYPE } from "@gtm-agents/gtm-ui/theme";
import { Suspense, lazy, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AuthGuard } from "./components/AuthGuard.js";
import { NavBar } from "./components/NavBar.js";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary.js";
import { PageSkeleton } from "./components/skeletons/PageSkeleton.js";
import { ChatProvider, useChatContext } from "./context/ChatContext.js";
import { ViewProvider } from "./context/ViewContext.js";
import { useActivityFeed, useGlobalSearch } from "./hooks/use-global-search.js";

// Top-level pages
const AuthCallbackPage = lazy(() =>
  import("./pages/AuthCallbackPage.js").then((m) => ({ default: m.AuthCallbackPage })),
);
const HomePage = lazy(() => import("./pages/HomePage.js").then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() =>
  import("./pages/LoginPage.js").then((m) => ({ default: m.LoginPage })),
);

// Pipeline
const PipelinePage = lazy(() =>
  import("./pages/pipeline/PipelinePage.js").then((m) => ({ default: m.PipelinePage })),
);
const ForecastPage = lazy(() =>
  import("./pages/revenue/ForecastPage.js").then((m) => ({ default: m.ForecastPage })),
);

// TOF Intelligence
const LeadsPage = lazy(() =>
  import("./pages/leads/LeadsPage.js").then((m) => ({ default: m.LeadsPage })),
);
const ProspectingPage = lazy(() =>
  import("./pages/prospecting/ProspectingPage.js").then((m) => ({ default: m.ProspectingPage })),
);
const SuppressionsPage = lazy(() =>
  import("./pages/leads/SuppressionsPage.js").then((m) => ({ default: m.SuppressionsPage })),
);

// Growth
const CampaignsPage = lazy(() =>
  import("./pages/growth/CampaignsPage.js").then((m) => ({ default: m.CampaignsPage })),
);

// Marketing
const AttributionPage = lazy(() =>
  import("./pages/marketing/AttributionPage.js").then((m) => ({ default: m.AttributionPage })),
);
const ContentPage = lazy(() =>
  import("./pages/marketing/ContentPage.js").then((m) => ({ default: m.ContentPage })),
);
const EventsPage = lazy(() =>
  import("./pages/marketing/EventsPage.js").then((m) => ({ default: m.EventsPage })),
);
const MarketingPage = lazy(() =>
  import("./pages/marketing/MarketingPage.js").then((m) => ({ default: m.MarketingPage })),
);

// Customers
const AtRiskPage = lazy(() =>
  import("./pages/customers/AtRiskPage.js").then((m) => ({ default: m.AtRiskPage })),
);
const CustomersOverviewPage = lazy(() =>
  import("./pages/customers/CustomersOverviewPage.js").then((m) => ({
    default: m.CustomersOverviewPage,
  })),
);
const FeedbackPage = lazy(() =>
  import("./pages/customers/FeedbackPage.js").then((m) => ({ default: m.FeedbackPage })),
);
const OnboardingPage = lazy(() =>
  import("./pages/customers/OnboardingPage.js").then((m) => ({ default: m.OnboardingPage })),
);
const RenewalsPage = lazy(() =>
  import("./pages/customers/RenewalsPage.js").then((m) => ({ default: m.RenewalsPage })),
);

// Accounts
const CompaniesPage = lazy(() =>
  import("./pages/accounts/CompaniesPage.js").then((m) => ({ default: m.CompaniesPage })),
);
const ContactDetailPage = lazy(() =>
  import("./pages/accounts/ContactDetailPage.js").then((m) => ({ default: m.ContactDetailPage })),
);
const ContactsPage = lazy(() =>
  import("./pages/accounts/ContactsPage.js").then((m) => ({ default: m.ContactsPage })),
);
const CompanyDetailPage = lazy(() =>
  import("./pages/accounts/CompanyDetailPage.js").then((m) => ({ default: m.CompanyDetailPage })),
);

// Partners
const PartnersPage = lazy(() =>
  import("./pages/partners/PartnersPage.js").then((m) => ({ default: m.PartnersPage })),
);

// Work
const DocumentsPage = lazy(() =>
  import("./pages/work/DocumentsPage.js").then((m) => ({ default: m.DocumentsPage })),
);
const TasksPage = lazy(() =>
  import("./pages/work/TasksPage.js").then((m) => ({ default: m.TasksPage })),
);

// Document Studio (embedded)
const StudioRoutes = lazy(() =>
  import("./pages/work/documents/studio/StudioRoutes.js").then((m) => ({
    default: m.StudioRoutes,
  })),
);

// Revenue
const RevenuePage = lazy(() =>
  import("./pages/revenue/RevenuePage.js").then((m) => ({ default: m.RevenuePage })),
);
const TargetsPage = lazy(() =>
  import("./pages/revenue/TargetsPage.js").then((m) => ({ default: m.TargetsPage })),
);

// People
const CapacityPage = lazy(() =>
  import("./pages/people/CapacityPage.js").then((m) => ({ default: m.CapacityPage })),
);
const HiringPage = lazy(() =>
  import("./pages/people/HiringPage.js").then((m) => ({ default: m.HiringPage })),
);
const OnboardingInternalPage = lazy(() =>
  import("./pages/people/OnboardingInternalPage.js").then((m) => ({
    default: m.OnboardingInternalPage,
  })),
);
const TeamPage = lazy(() =>
  import("./pages/people/TeamPage.js").then((m) => ({ default: m.TeamPage })),
);

// Ops
const AgentsHealthPage = lazy(() =>
  import("./pages/ops/AgentsHealthPage.js").then((m) => ({ default: m.AgentsHealthPage })),
);
const ObservabilityPage = lazy(() =>
  import("./pages/ops/ObservabilityPage.js").then((m) => ({ default: m.ObservabilityPage })),
);
const SystemHealthPage = lazy(() =>
  import("./pages/ops/SystemHealthPage.js").then((m) => ({ default: m.SystemHealthPage })),
);

// Settings
const HealthAdminPage = lazy(() =>
  import("./pages/settings/HealthAdminPage.js").then((m) => ({ default: m.HealthAdminPage })),
);
const IntegrationsPage = lazy(() =>
  import("./pages/settings/IntegrationsPage.js").then((m) => ({ default: m.IntegrationsPage })),
);
const ProfilePage = lazy(() =>
  import("./pages/settings/ProfilePage.js").then((m) => ({ default: m.ProfilePage })),
);
const TeamAdminPage = lazy(() =>
  import("./pages/settings/TeamAdminPage.js").then((m) => ({ default: m.TeamAdminPage })),
);

// Admin
const RequireAdmin = lazy(() =>
  import("./components/RequireAdmin.js").then((m) => ({ default: m.RequireAdmin })),
);
const RetentionPage = lazy(() =>
  import("./pages/admin/RetentionPage.js").then((m) => ({ default: m.RetentionPage })),
);
const TrashPage = lazy(() =>
  import("./pages/admin/TrashPage.js").then((m) => ({ default: m.TrashPage })),
);

function ShortcutsMount() {
  useShortcuts();
  return null;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const search = useGlobalSearch();
  const fetchActivity = useActivityFeed();
  return (
    <ViewProvider>
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          background: COLORS.bg,
          color: COLORS.text,
          fontFamily: TYPE.family.sans,
          fontSize: TYPE.size.md,
        }}
      >
        <NavBar />
        <main
          data-testid="app-main-content"
          // Fixed height (not minHeight) so `main` is the actual scroll
          // container — required for position:sticky headers inside pages to
          // pin. With minHeight the body scrolled instead and sticky never
          // engaged.
          style={{ flex: 1, overflowY: "auto", height: "100vh" }}
        >
          {children}
        </main>
        <CommandPalette onSearch={search} />
        <ActivityDrawer onFetchActivity={fetchActivity} />
        <ShortcutsMount />
      </div>
    </ViewProvider>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <AuthGuard>
      <AppShell>
        <ErrorBoundary>{children}</ErrorBoundary>
      </AppShell>
    </AuthGuard>
  );
}

function RedirectToLogin() {
  const location = useLocation();
  return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
}

function RedirectCompanyDetail() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/companies/${id}`} replace />;
}

function RedirectContactDetail() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/contacts/${id}`} replace />;
}

/** Redirect to `to`, carrying the current query string (so /tof?tab=leads →
 *  /leads?tab=leads). */
function RedirectPreservingQuery({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

function P({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

/** Full-screen protected route — auth guard only, no NavBar/AppShell */
function FS({ children }: { children: React.ReactNode }) {
  return (
    <AuthGuard>
      <ErrorBoundary>{children}</ErrorBoundary>
    </AuthGuard>
  );
}

function AppWithChat() {
  const { chatOpen, setChatOpen, pendingAssistant, pendingInitialInput } = useChatContext();
  const [conversationId, setConversationId] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "i") {
        e.preventDefault();
        setChatOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setChatOpen]);

  return (
    <>
      <AssistantPanel
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        defaultAssistant={pendingAssistant}
        initialInput={pendingInitialInput}
        conversationId={conversationId}
        onConversationChange={(id) => setConversationId(id ?? null)}
      />
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/callback"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <AuthCallbackPage />
            </Suspense>
          }
        />

        {/* Home */}
        <Route
          path="/"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <HomePage />
              </Suspense>
            </P>
          }
        />

        {/* Pipeline */}
        <Route
          path="/pipeline"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <PipelinePage />
              </Suspense>
            </P>
          }
        />
        <Route path="/pipeline/new" element={<Navigate to="/pipeline?deal=new" replace />} />
        <Route
          path="/forecast"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <ForecastPage />
              </Suspense>
            </P>
          }
        />

        {/* Growth — legacy redirects */}
        <Route path="/growth" element={<Navigate to="/leads" replace />} />
        <Route path="/growth/signals" element={<Navigate to="/leads" replace />} />
        <Route path="/growth/sequences" element={<Navigate to="/leads" replace />} />
        <Route path="/growth/campaigns" element={<Navigate to="/marketing/campaigns" replace />} />

        {/* Marketing */}
        <Route
          path="/marketing"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <MarketingPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/marketing/campaigns"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <CampaignsPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/marketing/:id"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <MarketingPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/marketing/content"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <ContentPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/marketing/events"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <EventsPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/marketing/attribution"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <AttributionPage />
              </Suspense>
            </P>
          }
        />

        {/* Customers */}
        <Route path="/customers/overview" element={<Navigate to="/customers" replace />} />
        <Route
          path="/customers"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <CustomersOverviewPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/customers/onboarding"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <OnboardingPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/customers/renewals"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <RenewalsPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/customers/expansion"
          element={<Navigate to="/pipeline?pipeline=expansion" replace />}
        />
        <Route
          path="/customers/at-risk"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <AtRiskPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/customers/feedback"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <FeedbackPage />
              </Suspense>
            </P>
          }
        />

        {/* Accounts */}
        <Route
          path="/companies"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <CompaniesPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/companies/:id"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <CompanyDetailPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/contacts"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <ContactsPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/contacts/:id"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <ContactDetailPage />
              </Suspense>
            </P>
          }
        />

        {/* Partners */}
        <Route
          path="/partners"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <PartnersPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/partners/:id"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <PartnersPage />
              </Suspense>
            </P>
          }
        />

        {/* Work */}
        <Route
          path="/tasks"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <TasksPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/documents"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <DocumentsPage />
              </Suspense>
            </P>
          }
        />

        {/* Document Studio — full-screen, no NavBar */}
        <Route
          path="/work/documents/studio/*"
          errorElement={<RouteErrorBoundary />}
          element={
            <FS>
              <Suspense fallback={<PageSkeleton />}>
                <StudioRoutes />
              </Suspense>
            </FS>
          }
        />

        {/* Revenue */}
        <Route
          path="/revenue"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <RevenuePage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/revenue/targets"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <TargetsPage />
              </Suspense>
            </P>
          }
        />

        {/* People */}
        <Route
          path="/people/hiring"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <HiringPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/people/onboarding"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <OnboardingInternalPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/people/team"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <TeamPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/people/capacity"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <CapacityPage />
              </Suspense>
            </P>
          }
        />

        {/* Ops */}
        <Route
          path="/ops/agents"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <AgentsHealthPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/ops/system"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <SystemHealthPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/ops/observability"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <ObservabilityPage />
              </Suspense>
            </P>
          }
        />

        {/* Settings */}
        <Route
          path="/settings/profile"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <ProfilePage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/settings/integrations"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <IntegrationsPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/settings/team"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <TeamAdminPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/settings/health"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <HealthAdminPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/settings/trash"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <RequireAdmin>
                  <TrashPage />
                </RequireAdmin>
              </Suspense>
            </P>
          }
        />
        <Route
          path="/settings/retention"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <RequireAdmin>
                  <RetentionPage />
                </RequireAdmin>
              </Suspense>
            </P>
          }
        />

        {/* 301 redirects for old URLs */}
        <Route path="/accounts" element={<Navigate to="/companies" replace />} />
        <Route path="/accounts/companies" element={<Navigate to="/companies" replace />} />
        <Route path="/accounts/:id" element={<RedirectCompanyDetail />} />
        <Route path="/deals" element={<Navigate to="/pipeline?view=list" replace />} />
        {/*
          TOF (Leads) is open to all authenticated users. Per-action permissions
          fall back to the RBAC harness (wired for the team in dev.sh / RBAC_RULES;
          permissive when RBAC_RULES is unset). The Inbox sub-tab remains admin-only
          (gated inside LeadsPage). Settings/Trash/Retention stay RequireAdmin below.
        */}
        <Route
          path="/leads"
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <LeadsPage />
              </Suspense>
            </P>
          }
        />
        <Route
          path="/leads/suppressions"
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <SuppressionsPage />
              </Suspense>
            </P>
          }
        />
        {/* Prospecting — account-first outbound (sibling to Leads under Growth). */}
        <Route
          path="/prospecting"
          errorElement={<RouteErrorBoundary />}
          element={
            <P>
              <Suspense fallback={<PageSkeleton />}>
                <ProspectingPage />
              </Suspense>
            </P>
          }
        />
        {/* Legacy /tof → /leads (keeps the ?tab= query so deep links survive). */}
        <Route path="/tof" element={<RedirectPreservingQuery to="/leads" />} />
        <Route path="/tof/suppressions" element={<Navigate to="/leads/suppressions" replace />} />
        <Route path="/agents" element={<Navigate to="/ops/agents" replace />} />
        <Route path="/outreach" element={<Navigate to="/prospecting" replace />} />
        <Route path="/prospects" element={<Navigate to="/companies?filter=prospect" replace />} />
        <Route
          path="/prospects/companies"
          element={<Navigate to="/companies?filter=prospect" replace />}
        />
        <Route path="/prospects/companies/:id" element={<RedirectCompanyDetail />} />
        <Route path="/prospects/:id" element={<RedirectContactDetail />} />

        <Route path="*" element={<RedirectToLogin />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ChatProvider>
      <AppWithChat />
    </ChatProvider>
  );
}
